/**
 * Connecting a source, in the four steps it actually is.
 *
 *   1. which app                  2. which connection to it
 *   3. which tables inside it     4. which node type each one becomes
 *
 * Numbered, because this one genuinely is a sequence: you cannot pick a table
 * before you have picked a connection, and a mapping cannot exist before the
 * table does. That is the one place in this product where an ordinal carries
 * information rather than decoration.
 *
 * Step three takes **several** objects, because a database has more than one
 * table and a workspace more than one folder. Each is mapped on its own — its
 * own node type, its own columns — but they all belong to **one source**,
 * because they come from one connection and are published together. The tab
 * lists the connection; opening it lists what it reads.
 *
 * The same component edits an existing source. Passing `editing` fills every
 * answer in and lands you on the mapping step: the form you change something in
 * is the form you made it in, so there is no second screen to disagree with.
 *
 * The last step ends in two buttons, and the difference between them is the
 * whole point of having two:
 *
 *   **Save**    writes the sources and their mappings. Nothing is fetched.
 *   **Publish** writes them, fetches the rows, and runs them through the rules.
 */

import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ErrorNotice, Logo, Modal, SkeletonRows } from '@/shared/components'
import { useToast } from '@/shared/components/toast'
import { Chip, Field } from '../components'
import { typesAgree, whyUnavailable } from './mapping'
import {
  useConnectionObjects,
  usePreviewObject,
  useSourceConnectors,
  useSourceMutations,
} from '../hooks'
import type {
  BrainSource,
  EdgeTypeDef,
  GraphModel,
  ImportScopeOption,
  NodeTypeDef,
  ObjectPreview,
  SourceConnector,
  SourceLink,
} from '../types/brain.types'

type Step = 1 | 2 | 3 | 4

const STEPS: { n: Step; label: string }[] = [
  { n: 1, label: 'Connector' },
  { n: 2, label: 'Connection' },
  { n: 3, label: 'Objects' },
  { n: 4, label: 'Map to nodes' },
]

/** What one chosen object is going to become. */
interface Plan {
  nodeTypeKey: string
  /** Source column -> node property. An empty target means "leave it out". */
  mapping: Record<string, string>
  /** Columns that point at another record rather than describing this one. */
  links: SourceLink[]
  preview: ObjectPreview | null
  loading: boolean
}

const EMPTY_PLAN: Plan = {
  nodeTypeKey: '',
  mapping: {},
  links: [],
  preview: null,
  loading: false,
}

export function SourceWizard({
  graphId,
  model,
  editing,
  onClose,
}: {
  graphId: string
  model: GraphModel
  /** An existing source to change, rather than a new one to create. */
  editing?: BrainSource
  onClose: () => void
}) {
  const navigate = useNavigate()
  const toast = useToast()

  const connectors = useSourceConnectors(graphId)
  const { attach, update, publish } = useSourceMutations(graphId)
  const previewObject = usePreviewObject(graphId)

  // Editing starts where creating ends: the connector and connection are
  // already decided, so the flow opens on the step that is actually in question.
  const [step, setStep] = useState<Step>(editing ? 3 : 1)
  const [connector, setConnector] = useState<SourceConnector | null>(null)
  const [connectionId, setConnectionId] = useState<string | null>(
    editing?.connection_id ?? null,
  )
  const [chosen, setChosen] = useState<ImportScopeOption[]>([])
  const [objectInput, setObjectInput] = useState<Record<string, Record<string, string>>>(() =>
    Object.fromEntries(
      (editing?.objects ?? []).map((o) => [
        o.action_id,
        Object.fromEntries(Object.entries(o.input ?? {}).map(([k, v]) => [k, String(v ?? '')])),
      ]),
    ),
  )
  const [plans, setPlans] = useState<Record<string, Plan>>(() =>
    Object.fromEntries(
      (editing?.objects ?? []).map((o) => [
        o.action_id,
        {
          nodeTypeKey: o.node_type_key ?? '',
          mapping: { ...(o.mapping ?? {}) },
          links: [...(o.links ?? [])],
          preview: null,
          loading: false,
        },
      ]),
    ),
  )
  const [busy, setBusy] = useState<'save' | 'publish' | null>(null)

  const objects = useConnectionObjects(graphId, step >= 3 ? connectionId : null)

  // Coming back from creating a connection, the new one should be waiting here
  // rather than needing the wizard restarted.
  const connectorNow = useMemo(
    () => connectors.data?.find((c) => c.connector_id === connector?.connector_id) ?? connector,
    [connectors.data, connector],
  )

  // The objects an existing source already reads, ticked as soon as the
  // connector tells us what they are called.
  const listed = objects.data?.objects
  useEffect(() => {
    if (!editing || !listed || chosen.length) return
    const wanted = new Set((editing.objects ?? []).map((o) => o.action_id))
    setChosen(listed.filter((o) => wanted.has(o.action_id)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, listed])

  function toggleObject(scope: ImportScopeOption) {
    setChosen((current) =>
      current.some((o) => o.action_id === scope.action_id)
        ? current.filter((o) => o.action_id !== scope.action_id)
        : [...current, scope],
    )
  }

  /** Read a few rows from every chosen object, so each mapping has columns. */
  useEffect(() => {
    if (step !== 4 || !connectionId) return
    for (const scope of chosen) {
      if (plans[scope.action_id]?.preview || plans[scope.action_id]?.loading) continue
      setPlans((prev) => ({
        ...prev,
        [scope.action_id]: { ...(prev[scope.action_id] ?? EMPTY_PLAN), loading: true },
      }))
      previewObject
        .mutateAsync({
          connection_id: connectionId,
          action_id: scope.action_id,
          input: cleaned(objectInput[scope.action_id]),
        })
        .then((preview) =>
          setPlans((prev) => ({
            ...prev,
            [scope.action_id]: {
              ...(prev[scope.action_id] ?? EMPTY_PLAN),
              preview,
              loading: false,
            },
          })),
        )
        .catch(() =>
          setPlans((prev) => ({
            ...prev,
            [scope.action_id]: { ...(prev[scope.action_id] ?? EMPTY_PLAN), loading: false },
          })),
        )
    }
    // Previews belong to the chosen objects and this connection; nothing else re-runs them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, connectionId, chosen.map((o) => o.action_id).join(',')])

  function setPlan(actionId: string, patch: Partial<Plan>) {
    setPlans((prev) => ({
      ...prev,
      [actionId]: { ...(prev[actionId] ?? EMPTY_PLAN), ...patch },
    }))
  }

  /** Every chosen object has been pointed at a node type. */
  const ready = chosen.length > 0 && chosen.every((o) => plans[o.action_id]?.nodeTypeKey)

  const canContinue =
    (step === 1 && Boolean(connector)) ||
    (step === 2 && Boolean(connectionId)) ||
    (step === 3 && chosen.length > 0) ||
    step === 4

  /**
   * One source, carrying every object.
   *
   * A connection is the source; the tables inside it are what it reads. Saving
   * them as one row is what lets the tab list "the warehouse" rather than four
   * near-identical rows nobody asked for, and it is what makes publishing the
   * source mean publishing all of it.
   */
  function payload() {
    return {
      connection_id: connectionId!,
      display_name:
        editing?.display_name ?? `${objects.data?.label ?? connector?.name ?? 'Source'}`,
      kind: objects.data?.suggested_kind,
      mode: objects.data?.suggested_mode,
      // The first object's node type also answers the source-level hint, which
      // the extract path and the pasted-rows dialog still read.
      entity_hint: plans[chosen[0]?.action_id ?? '']?.nodeTypeKey || undefined,
      objects: chosen.map((scope) => {
        const plan = plans[scope.action_id] ?? EMPTY_PLAN
        return {
          action_id: scope.action_id,
          label: scope.label,
          input: cleaned(objectInput[scope.action_id]),
          node_type_key: plan.nodeTypeKey,
          mapping: plan.mapping,
          links: plan.links,
        }
      }),
    }
  }

  async function saveSource() {
    if (!connectionId) return null
    if (editing) {
      const body = payload()
      return update.mutateAsync({
        id: editing.id,
        body: {
          display_name: body.display_name,
          entity_hint: body.entity_hint,
          objects: body.objects,
        },
      })
    }
    return attach.mutateAsync({
      ...payload(),
      source_id: `${connectionId}`.slice(0, 64),
      // Kept current from the start: a source someone attaches is a source
      // they want to see new rows from, without coming back to press a button.
      sync_mode: 'poll',
      poll_interval_s: 300,
    })
  }

  async function onSave() {
    setBusy('save')
    try {
      await saveSource()
      toast.success(
        editing ? 'Source updated' : 'Source saved',
        editing
          ? `${chosen.length} object${chosen.length === 1 ? '' : 's'} mapped.`
          : `${chosen.length} object${chosen.length === 1 ? '' : 's'} mapped. Auto-sync is on — the first read starts within a minute, and new rows keep arriving every 5 minutes. Publish to fetch now.`,
      )
      onClose()
    } catch (error) {
      toast.error(error)
    } finally {
      setBusy(null)
    }
  }

  async function onPublish() {
    setBusy('publish')
    try {
      const source = await saveSource()
      if (!source) return
      const outcome = await publish.mutateAsync({ id: source.id })
      const buckets = (outcome.counts?.buckets ?? {}) as Record<string, number>

      if (outcome.failed.length) {
        toast.error(
          new Error(outcome.failed.map((f) => f.error ?? 'failed').join('; ')),
          `${outcome.published} of ${outcome.objects.length} objects fetched`,
        )
      } else {
        toast.success(
          'Published',
          `${outcome.staged} fetched · ${buckets.rejected ?? 0} rejected · ${
            buckets.potential ?? 0
          } waiting for review`,
        )
      }
      onClose()
    } catch (error) {
      toast.error(error)
    } finally {
      setBusy(null)
    }
  }

  return (
    <Modal
      title={editing ? `Edit ${editing.display_name}` : 'Connect a source'}
      onClose={onClose}
      wide
      footer={
        <>
          {/* Editing cannot walk back past the object step: the connector and
              the connection are what this source *is*, and changing them would
              be a different source. */}
          {step > (editing ? 3 : 1) && (
            <button
              className="btn btn-ghost"
              onClick={() => setStep((s) => (s - 1) as Step)}
              disabled={Boolean(busy)}
            >
              Back
            </button>
          )}
          <span className="cp-spacer" />
          {step < 4 ? (
            <button
              className="btn btn-primary"
              disabled={!canContinue}
              onClick={() => setStep((s) => (s + 1) as Step)}
            >
              Continue
              {step === 3 && chosen.length > 0 && ` with ${chosen.length}`}
            </button>
          ) : (
            <>
              {/* Two endings, weighted by what they do. */}
              <button className="btn" onClick={onSave} disabled={Boolean(busy) || !ready}>
                {busy === 'save' ? 'Saving…' : 'Save'}
              </button>
              <button
                className="btn btn-primary"
                onClick={onPublish}
                disabled={Boolean(busy) || !ready}
              >
                {busy === 'publish' ? 'Publishing…' : 'Publish'}
              </button>
            </>
          )}
        </>
      }
    >
      <ol className="wz-steps">
        {(editing ? STEPS.filter((s) => s.n >= 3) : STEPS).map(({ n, label }) => (
          <li key={n} className="wz-step" data-state={n === step ? 'on' : n < step ? 'done' : ''}>
            <span className="wz-n">{n}</span>
            <span className="wz-l">{label}</span>
          </li>
        ))}
      </ol>

      {/* ------------------------------------------------ 1. the connector */}
      {step === 1 && (
        <div className="stack">
          <p className="small muted" style={{ margin: 0 }}>
            Which app the records come from. A connector with nothing readable is shown but not
            selectable — there would be nothing to pick at step three.
          </p>
          {connectors.isLoading && (
            <SkeletonRows rows={6} columns={3} label="Loading connectors…" />
          )}
          {connectors.error && (
            <ErrorNotice error={connectors.error} onRetry={connectors.refetch} />
          )}
          <div className="wz-grid">
            {connectors.data?.map((c) => (
              <button
                key={c.connector_id}
                className="wz-card"
                data-on={connector?.connector_id === c.connector_id}
                disabled={!c.importable}
                onClick={() => {
                  setConnector(c)
                  setConnectionId(c.connections[0]?.connection_id ?? null)
                }}
              >
                <Logo id={c.connector_id} name={c.name} small />
                <span className="wz-cname">{c.name}</span>
                <span className="wz-cmeta">
                  {c.importable
                    ? `${c.object_count} object${c.object_count === 1 ? '' : 's'}`
                    : 'nothing readable'}
                  {c.connections.length > 0 &&
                    ` · ${c.connections.length} connection${c.connections.length === 1 ? '' : 's'}`}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ----------------------------------------------- 2. the connection */}
      {step === 2 && connectorNow && (
        <div className="stack">
          <p className="small muted" style={{ margin: 0 }}>
            Which {connectorNow.name} account to read from. Credentials stay where they are — a
            source is a routing decision, not a copy of them.
          </p>

          {connectorNow.connections.length === 0 && (
            <p className="small faint" style={{ margin: 0 }}>
              You have no {connectorNow.name} connections yet.
            </p>
          )}

          <div className="stack" style={{ gap: 6 }}>
            {connectorNow.connections.map((connection) => (
              <button
                key={connection.connection_id}
                className="wz-row"
                data-on={connectionId === connection.connection_id}
                onClick={() => setConnectionId(connection.connection_id)}
              >
                <Logo id={connectorNow.connector_id} name={connectorNow.name} small />
                <span className="nm">{connection.label}</span>
                <Chip tone={connection.status === 'active' ? 'ok' : 'warn'}>
                  {connection.status}
                </Chip>
              </button>
            ))}
          </div>

          {/* Creating one is part of this flow rather than an errand you are
              sent on: the connect page comes back here when it is done. */}
          <button
            className="btn"
            onClick={() =>
              navigate(
                `/connections/new/${connectorNow.connector_id}?return=${encodeURIComponent(
                  `/brain/${graphId}?tab=sources`,
                )}`,
              )
            }
          >
            + Connect a new {connectorNow.name} account
          </button>
        </div>
      )}

      {/* -------------------------------------------------- 3. the objects */}
      {step === 3 && (
        <div className="stack">
          <p className="small muted" style={{ margin: 0 }}>
            What to read: tables, folders, lookups. Pick as many as you need — each one becomes
            its own source, mapped to its own node type on the next step.
          </p>
          {objects.isLoading && <SkeletonRows rows={4} columns={2} label="Looking inside…" />}
          {objects.error && <ErrorNotice error={objects.error} onRetry={objects.refetch} />}
          {objects.data?.objects.length === 0 && (
            <p className="small faint">This connection exposes nothing importable.</p>
          )}

          <div className="stack" style={{ gap: 6 }}>
            {objects.data?.objects.map((scope) => {
              const on = chosen.some((o) => o.action_id === scope.action_id)
              return (
                <div key={scope.action_id} className="stack" style={{ gap: 6 }}>
                  <button
                    className="wz-row"
                    data-on={on}
                    role="checkbox"
                    aria-checked={on}
                    onClick={() => toggleObject(scope)}
                  >
                    <span className="wz-check" aria-hidden="true">
                      {on ? '✓' : ''}
                    </span>
                    <span className="nm">{scope.label}</span>
                    <span className="ds truncate">{scope.summary}</span>
                  </button>

                  {/* An object that needs a space, a folder or a channel asks
                      here, before anything is fetched. */}
                  {on &&
                    scope.input_schema?.properties &&
                    Object.entries(
                      scope.input_schema.properties as Record<string, never>,
                    ).map(([key, spec]) => (
                      <Field
                        key={key}
                        label={(spec as { title?: string }).title ?? key}
                        help={(spec as { description?: string }).description}
                      >
                        <input
                          className="input"
                          value={objectInput[scope.action_id]?.[key] ?? ''}
                          onChange={(e) =>
                            setObjectInput((prev) => ({
                              ...prev,
                              [scope.action_id]: {
                                ...(prev[scope.action_id] ?? {}),
                                [key]: e.target.value,
                              },
                            }))
                          }
                        />
                      </Field>
                    ))}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* ----------------------------------------------- 4. map to nodes */}
      {step === 4 && (
        <div className="stack">
          <p className="small muted" style={{ margin: 0 }}>
            Each object becomes one node type. Columns are matched by name where they line up;
            change anything that is wrong.
          </p>

          {chosen.map((scope) => (
            <ObjectMapping
              key={scope.action_id}
              scope={scope}
              plan={plans[scope.action_id] ?? EMPTY_PLAN}
              nodeTypes={model.node_types}
              edgeTypes={model.edge_types}
              onChange={(patch) => setPlan(scope.action_id, patch)}
            />
          ))}

          <div className="wz-endings">
            <p className="small">
              <b>Save</b> writes {chosen.length === 1 ? 'this source' : 'these sources'} and their
              mappings. No records are fetched.
            </p>
            <p className="small">
              <b>Publish</b> fetches the rows now and runs them through this graph's quality,
              matching and survivorship rules.
            </p>
          </div>
        </div>
      )}
    </Modal>
  )
}

/** One chosen object: the node type it becomes, and how its columns line up. */
function ObjectMapping({
  scope,
  plan,
  nodeTypes,
  edgeTypes,
  onChange,
}: {
  scope: ImportScopeOption
  plan: Plan
  nodeTypes: NodeTypeDef[]
  edgeTypes: EdgeTypeDef[]
  onChange: (patch: Partial<Plan>) => void
}) {
  const nodeType = nodeTypes.find((n) => n.key === plan.nodeTypeKey)
  const columns = plan.preview?.columns ?? []

  /**
   * The relationships this node type can be at the near end of.
   *
   * A link has to be an edge type the model already allows — the mapping step
   * says which column carries the key, not what the model is.
   */
  const outgoing = useMemo(
    () =>
      // Every relationship this type takes part in, in either direction. An
      // edge declared Space → Page is a perfectly reasonable thing to produce
      // from a column on the *page*, and the resolver writes it the way the
      // model declares it — so refusing to offer it here would only hide a
      // relationship someone has already modelled.
      edgeTypes
        .filter(
          (e) =>
            e.from_node_type === plan.nodeTypeKey || e.to_node_type === plan.nodeTypeKey,
        )
        .map((e) => ({
          ...e,
          // Which end the key points at, from this type's side.
          far: e.from_node_type === plan.nodeTypeKey ? e.to_node_type : e.from_node_type,
        })),
    [edgeTypes, plan.nodeTypeKey],
  )

  function linkFor(property: string): SourceLink | undefined {
    return plan.links.find((l) => l.property_key === property)
  }

  function setLink(property: string, next: SourceLink | null) {
    const rest = plan.links.filter((l) => l.property_key !== property)
    onChange({ links: next ? [...rest, next] : rest })
  }

  /** Property -> the column that already claims it. One column each. */
  const takenBy = useMemo(() => {
    const out: Record<string, string> = {}
    for (const [column, property] of Object.entries(plan.mapping)) {
      if (property) out[property] = column
    }
    return out
  }, [plan.mapping])

  /**
   * A first guess at the mapping: exact matches once both sides are normalised,
   * so "Account Name" finds `account_name`. Only ever a starting point.
   */
  useEffect(() => {
    if (!nodeType || !columns.length || Object.keys(plan.mapping).length) return
    const normalise = (text: string) => text.toLowerCase().replace(/[^a-z0-9]+/g, '_')
    const guess: Record<string, string> = {}
    const claimed = new Set<string>()
    for (const column of columns) {
      // The guess obeys the same two rules the selects do: one column per
      // property, and only where the types agree.
      const match = nodeType.properties.find(
        (p) =>
          normalise(p.key) === normalise(column.name) &&
          !claimed.has(p.key) &&
          typesAgree(column.detected_type, p.data_type),
      )
      if (match) {
        guess[column.name] = match.key
        claimed.add(match.key)
      }
    }
    if (Object.keys(guess).length) onChange({ mapping: guess })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan.nodeTypeKey, columns.length])

  return (
    <div className="wz-map">
      <div className="wz-maphead">
        <strong>{scope.label}</strong>
        <span className="faint small">→</span>
        <select
          className="select"
          style={{ maxWidth: 220 }}
          value={plan.nodeTypeKey}
          aria-label={`Node type for ${scope.label}`}
          onChange={(e) => onChange({ nodeTypeKey: e.target.value, mapping: {} })}
        >
          <option value="">Choose a node type…</option>
          {nodeTypes.map((n) => (
            <option key={n.node_type_id} value={n.key}>
              {n.key}
            </option>
          ))}
        </select>
      </div>

      {plan.loading && <SkeletonRows rows={3} columns={4} label="Reading a few rows…" />}

      {plan.preview && !plan.preview.ok && (
        <p className="small" style={{ color: 'var(--warn)', margin: 0 }}>
          {plan.preview.error} — you can still save the mapping and fix the connection afterwards.
        </p>
      )}

      {nodeType && plan.preview?.ok && columns.length > 0 && (
        <div className="table-wrap">
          <table className="cp-table">
            <thead>
              <tr>
                <th>Source column</th>
                <th>Looks like</th>
                <th>Sample</th>
                <th>Becomes</th>
              </tr>
            </thead>
            <tbody>
              {columns.map((column) => {
                const target = plan.mapping[column.name] ?? ''
                return (
                  <tr key={column.name}>
                    <td className="mono small">{column.name}</td>
                    <td className="small muted">{column.detected_type}</td>
                    <td className="mono small truncate" style={{ maxWidth: 180 }}>
                      {plan.preview!.rows
                        .map((r) => r[column.name])
                        .filter((v) => v !== undefined && v !== null && v !== '')
                        .slice(0, 3)
                        .map((v) => String(v))
                        .join(' · ') || '—'}
                    </td>
                    <td>
                      <select
                        className="select"
                        value={target}
                        aria-label={`What ${column.name} becomes`}
                        onChange={(e) =>
                          onChange({
                            mapping: { ...plan.mapping, [column.name]: e.target.value },
                          })
                        }
                      >
                        <option value="">— ignore —</option>
                        {nodeType.properties.map((p) => {
                          // A property another column already claims, or one
                          // whose type this column cannot fill, is shown but
                          // not selectable — hiding it would leave someone
                          // hunting for a field they can see in the model.
                          const claimedBy = takenBy[p.key]
                          const reason =
                            p.key === target
                              ? null
                              : whyUnavailable(column.detected_type, p.data_type, claimedBy)
                          return (
                            <option key={p.property_id} value={p.key} disabled={Boolean(reason)}>
                              {p.key}
                              {reason ? ` — ${reason}` : ''}
                            </option>
                          )
                        })}
                      </select>

                      {/* A column can be more than a value. `spaceid` is not a
                          fact about the page — it is the space the page is in,
                          and saying so here is what turns it into an edge. */}
                      {target && outgoing.length > 0 && (
                        <LinkControl
                          property={target}
                          link={linkFor(target)}
                          outgoing={outgoing}
                          nodeTypes={nodeTypes}
                          onChange={(next) => setLink(target, next)}
                        />
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {nodeType && plan.preview?.ok && columns.length > 0 && (
        <p className="small faint" style={{ margin: 0 }}>
          A column left as <b>ignore</b> is dropped. A column you do not map keeps its own name,
          so an object that already matches the model needs no mapping.
        </p>
      )}
    </div>
  )
}

/** Blank inputs are absent inputs, not empty strings the provider has to reject. */
function cleaned(input: Record<string, string> | undefined): Record<string, unknown> {
  return Object.fromEntries(Object.entries(input ?? {}).filter(([, v]) => v !== ''))
}

/**
 * "This column points at another record."
 *
 * Off by default, because most columns are values. Turning it on asks the two
 * things an edge needs that a column cannot imply: which relationship it is,
 * and what the value matches at the far end.
 */
/** An edge type, plus which end of it is the *other* one from here. */
type LinkOption = EdgeTypeDef & { far: string }

function LinkControl({
  property,
  link,
  outgoing,
  nodeTypes,
  onChange,
}: {
  property: string
  link: SourceLink | undefined
  outgoing: LinkOption[]
  nodeTypes: NodeTypeDef[]
  onChange: (next: SourceLink | null) => void
}) {
  const edge = outgoing.find((e) => e.key === link?.edge_type_key) ?? outgoing[0]
  const target = nodeTypes.find((n) => n.key === (link?.to_node_type ?? edge?.far))

  if (!edge) return null

  return (
    <div className="lk">
      <label className="lk-toggle">
        <input
          type="checkbox"
          checked={Boolean(link)}
          onChange={(e) =>
            onChange(
              e.target.checked
                ? {
                    property_key: property,
                    edge_type_key: edge.key,
                    to_node_type: edge.far,
                    // The far end's own key is the sensible default: a foreign
                    // key almost always points at whatever that type is keyed on.
                    to_property:
                      nodeTypes
                        .find((n) => n.key === edge.far)
                        ?.properties.find((p) => p.is_primary_key)?.key ??
                      nodeTypes.find((n) => n.key === edge.far)?.properties[0]?.key ??
                      '',
                  }
                : null,
            )
          }
        />
        <span>points at another record</span>
      </label>

      {link && (
        <div className="lk-body">
          <select
            className="select"
            aria-label={`Relationship for ${property}`}
            value={link.edge_type_key}
            onChange={(e) => {
              const chosen = outgoing.find((o) => o.key === e.target.value)
              if (!chosen) return
              onChange({
                ...link,
                edge_type_key: chosen.key,
                to_node_type: chosen.far,
                to_property:
                  nodeTypes
                    .find((n) => n.key === chosen.far)
                    ?.properties.find((p) => p.is_primary_key)?.key ?? '',
              })
            }}
          >
            {outgoing.map((e) => (
              <option key={e.edge_type_id} value={e.key}>
                {e.key} → {e.far}
              </option>
            ))}
          </select>
          <span className="lk-matches">matching</span>
          <select
            className="select"
            aria-label={`What ${property} matches on ${link.to_node_type}`}
            value={link.to_property}
            onChange={(e) => onChange({ ...link, to_property: e.target.value })}
          >
            {(target?.properties ?? []).map((p) => (
              <option key={p.property_id} value={p.key}>
                {p.key}
              </option>
            ))}
          </select>

          {/* A column holding several keys at once. A *list* needs nothing
              here — a list is already many values — but splitting a string is
              a guess, so it only happens when someone says which character to
              split on. */}
          <label className="lk-sep">
            <span className="small faint">many keys, split on</span>
            <input
              className="input"
              maxLength={4}
              placeholder="none"
              aria-label={`Separator for ${property}`}
              value={link.separator ?? ''}
              onChange={(e) =>
                onChange({ ...link, separator: e.target.value || null })
              }
            />
          </label>
        </div>
      )}
    </div>
  )
}
